import React, { useState, useEffect, useMemo, useRef } from 'react';
import Container from '@material-ui/core/Container';
import Board from '@asseinfo/react-kanban'
import '@asseinfo/react-kanban/dist/styles.css'
import api from '../../services/api';
import { pode, mensagemErro } from '../../services/acesso';
import { Link } from "react-router-dom";
import { toast } from 'react-toastify';
import { TextField, Button, ButtonGroup, Grid, LinearProgress, Paper, Table, TableHead, TableBody, TableRow, TableCell, Tooltip, IconButton, Collapse, Menu, MenuItem } from '@material-ui/core';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AddIcon from '@material-ui/icons/Add';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import PageHeader from '../../components/PageHeader';
import FilterBar from '../../components/FilterBar';
import StatusChip from '../../components/StatusChip';
import LeadDrawer from './LeadDrawer';
import ContatosLead from '../../components/ContatosLead';
import { lerFiltroBoard, salvarFiltroBoard, limparFiltroBoard } from '../../utils/memoriaFiltro';
import { situacaoFollowUp, iniciais } from '../../utils/followUp';
import { CORES } from '../../theme';

const ESTADOS = [
  ["AC", "Acre"], ["AL", "Alagoas"], ["AP", "Amapá"], ["AM", "Amazonas"], ["BA", "Bahia"], ["CE", "Ceará"],
  ["DF", "Distrito Federal"], ["ES", "Espírito Santo"], ["GO", "Goiás"], ["MA", "Maranhão"], ["MT", "Mato Grosso"],
  ["MS", "Mato Grosso do Sul"], ["MG", "Minas Gerais"], ["PA", "Pará"], ["PB", "Paraíba"], ["PR", "Paraná"],
  ["PE", "Pernambuco"], ["PI", "Piauí"], ["RJ", "Rio de Janeiro"], ["RN", "Rio Grande do Norte"],
  ["RS", "Rio Grande do Sul"], ["RO", "Rondônia"], ["RR", "Roraima"], ["SC", "Santa Catarina"], ["SP", "São Paulo"],
  ["SE", "Sergipe"], ["TO", "Tocantins"],
].map(([sigla, nome]) => ({ sigla, nome }));

const COLUNAS_INICIAIS = [
  { id: 8, title: 'Em Branco', cards: [] },
  { id: 1, title: 'Em andamento', cards: [] },
  { id: 2, title: 'Retorno futuro', cards: [] },
  { id: 3, title: 'Aguardando retorno', cards: [] },
  { id: 4, title: 'Sem interesse', cards: [] },
  { id: 5, title: 'Sem perfil', cards: [] },
  { id: 6, title: 'Black list', cards: [] },
  { id: 7, title: 'Reunião agendada', cards: [] },
];

// Colunas pouco usadas começam recolhidas (o usuário pode mostrar).
const OCULTAS_PADRAO = [5, 6];

const FILTROS_VAZIOS = { razao: "", cliente: null, mailing: null, assessor: null, estado: "", cidade: "", followUp: "" };

const TOM_FOLLOW = {
  erro: { background: '#FBE6DF', color: '#8E2F12' },
  alerta: { background: '#FFF3DC', color: '#7A4A05' },
  ok: { background: '#E4EFDF', color: '#1F4615' },
};

function changePosition(arr, from, to) {
  arr.splice(to, 0, arr.splice(from, 1)[0]);
  return arr;
}

const GestaoAssessor = () => {
  const [columns, setColumns] = useState({ columns: COLUNAS_INICIAIS });
  const [loading, setLoading] = useState(true);
  const [mailings, setMailings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [assessores, setAssessores] = useState([]);
  // mapa mailing_leads_id -> assessor escolhido nesta sessão (a API devolve "assessores" no board)
  const [assessorSelect, setAssessorSelect] = useState({});

  // Reabre o board no último Cliente + Mailing usados por este usuário.
  const filtroSalvo = useMemo(() => lerFiltroBoard(), []);
  const filtrosIniciais = { ...FILTROS_VAZIOS, cliente: filtroSalvo?.cliente || null, mailing: filtroSalvo?.mailing || null };

  const [filtros, setFiltros] = useState(filtrosIniciais);
  // O que foi pesquisado por último (as etiquetas mostram estes, não o que está só digitado).
  const [aplicados, setAplicados] = useState(filtrosIniciais);
  const ultimaBusca = useRef(0);
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [ocultas, setOcultas] = useState(OCULTAS_PADRAO);
  const [visao, setVisao] = useState("board");
  const [aberto, setAberto] = useState(null); // { card, colunaId }

  const alterar = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));

  /* Busca: TODOS os filtros vão juntos (antes a recarga automática descartava cliente, mailing e razão social). */
  const buscar = async (f = filtros) => {
    const minha = ++ultimaBusca.current;
    setAplicados(f);
    setLoading(true);
    if (f.mailing?.id) salvarFiltroBoard({ cliente: f.cliente, mailing: f.mailing });
    try {
      const result = await api.get(`board`, {
        params: {
          customer_id: f.cliente?.id,
          mailing_id: f.mailing?.id,
          corporate_name: f.razao || undefined,
          follow_up: f.followUp || undefined,
          id_assessor: localStorage.getItem("id_assessor"),
          assessor_filter_id: f.assessor?.id,
          cidade: f.cidade || undefined,
          estado: f.estado || undefined,
        }
      });
      if (minha !== ultimaBusca.current) return; // chegou depois de uma busca mais nova
      const data = result?.data || {};
      if (data.assessores) setAssessorSelect(data.assessores);
      setColumns(data.columns ? data : { columns: COLUNAS_INICIAIS });
    } catch (e) {
      if (minha === ultimaBusca.current) toast.error(mensagemErro(e, "Não foi possível carregar o board"));
    } finally {
      if (minha === ultimaBusca.current) setLoading(false);
    }
  };

  useEffect(() => {
    buscar(filtrosIniciais);
  }, []);

  useEffect(() => {
    // Filtros e atribuição: só assessores e clientes ativos.
    api.get("assessores", { params: { status: true } })
      .then((result) => setAssessores(result.data?.data || []))
      .catch((e) => toast.error(mensagemErro(e, "Não foi possível carregar os assessores")));
    api.get("customers", { params: { assessor_id: localStorage.getItem("id_assessor"), status: true } })
      .then((result) => setCustomers(result.data?.data || []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    api.get(`mailings`, { params: { assessor_id: localStorage.getItem("id_assessor"), customer_id: filtros.cliente?.id } })
      .then((result) => setMailings(result.data?.data || []))
      .catch(() => setMailings([]));
  }, [filtros.cliente]);

  const limpar = () => {
    limparFiltroBoard();
    setFiltros(FILTROS_VAZIOS);
    buscar(FILTROS_VAZIOS);
  };

  const remover = (campo) => () => {
    const novo = { ...aplicados, [campo]: FILTROS_VAZIOS[campo] };
    if (campo === "cliente") novo.mailing = null;
    if (campo === "cliente" || campo === "mailing") limparFiltroBoard();
    setFiltros(novo);
    buscar(novo);
  };

  const ativos = [
    aplicados.razao && { rotulo: `Razão social: ${aplicados.razao}`, remover: remover("razao") },
    aplicados.cliente && { rotulo: `Cliente: ${aplicados.cliente.name}`, remover: remover("cliente") },
    aplicados.mailing && { rotulo: `Mailing: ${aplicados.mailing.name}`, remover: remover("mailing") },
    aplicados.assessor && { rotulo: `Assessor: ${aplicados.assessor.name}`, remover: remover("assessor") },
    aplicados.estado && { rotulo: `Estado: ${aplicados.estado}`, remover: remover("estado") },
    aplicados.cidade && { rotulo: `Cidade: ${aplicados.cidade}`, remover: remover("cidade") },
    aplicados.followUp && { rotulo: `Follow-up: ${aplicados.followUp.split("-").reverse().join("/")}`, remover: remover("followUp") },
  ].filter(Boolean);

  /* Assessor ------------------------------------------------------------ */
  const assessorDoCard = (card) => {
    const escolhido = assessorSelect && assessorSelect[card.mailing_leads_id];
    if (escolhido && escolhido.name) return escolhido;
    if (card.assessor_name) return { id: card.id_assessor, name: card.assessor_name };
    return null;
  };

  const trocarAssessor = (card, novo) => {
    api.patch("board/" + card.mailing_leads_id + "/vincular-assessor", { assessor_id: novo?.id })
      .then(() => toast.success(novo ? `Lead atribuído a ${novo.name}` : "Assessor removido do lead"))
      .catch((error) => toast.error(mensagemErro(error, "Não foi possível alterar o assessor")));

    setAssessorSelect((m) => ({ ...m, [card.mailing_leads_id]: novo || "" }));
    // atualiza o card na tela sem recarregar o board inteiro
    setColumns((atual) => ({
      ...atual,
      columns: atual.columns.map((col) => ({
        ...col,
        cards: col.cards.map((c) => (c.mailing_leads_id === card.mailing_leads_id
          ? { ...c, id_assessor: novo?.id || null, assessor_name: novo?.name || "" }
          : c)),
      })),
    }));
  };

  /* Arrastar e soltar (mesma lógica de antes) ------------------------- */
  const updatePositionAndStatus = async (toColumnId, cards_positions, board, backup) => {
    try {
      await api.patch("board/" + board.id, { status: toColumnId, positions: JSON.stringify(cards_positions) });
      return true;
    } catch (e) {
      toast.error(mensagemErro(e, "Sem permissão"));
      setColumns(backup);
      return false;
    }
  };

  const onCardDragEnd = async (board, card, source) => {
    const backup = JSON.parse(JSON.stringify(columns));
    const { toColumnId, toPosition } = source;
    const { fromColumnId } = card;
    const n_coluns = JSON.parse(JSON.stringify(columns));

    const posicoesDaColuna = (lista) => {
      const indexCol = lista.findIndex(f => f.id == toColumnId); // eslint-disable-line eqeqeq
      if (indexCol < 0) return [];
      return lista[indexCol].cards.map((item, index) => ({ id_lead: item.id, position: index }));
    };

    if (toColumnId == fromColumnId) { // eslint-disable-line eqeqeq
      n_coluns.columns = n_coluns.columns.map(col => {
        if (col.id == toColumnId) { // eslint-disable-line eqeqeq
          const posicao_atual = col.cards.findIndex(fin => fin.id == board.id); // eslint-disable-line eqeqeq
          col.cards = changePosition(col.cards, posicao_atual, toPosition);
        }
        return col;
      });
    } else {
      n_coluns.columns = n_coluns.columns.map(col => {
        if (col.id == toColumnId) { // eslint-disable-line eqeqeq
          col.cards.push(board);
          col.cards = changePosition(col.cards, col.cards.length - 1, toPosition);
        }
        if (col.id == fromColumnId) { // eslint-disable-line eqeqeq
          col.cards = col.cards.filter(cc => cc.id != board.id); // eslint-disable-line eqeqeq
        }
        return col;
      });
    }

    setColumns(n_coluns);
    updatePositionAndStatus(toColumnId, posicoesDaColuna(n_coluns.columns), board, backup);
  };

  /* Mover sem arrastar: botão "Mover para" no card e situação no painel lateral.
     O card vai para o topo da coluna escolhida e grava como no arrastar. */
  const moverPara = (card, paraId) => {
    const lista = columns.columns || [];
    const origem = lista.find((c) => (c.cards || []).some((x) => x.id === card.id));
    if (!origem || origem.id === paraId) return;
    const backup = JSON.parse(JSON.stringify(columns));
    const novas = lista.map((col) => {
      if (col.id === origem.id) return { ...col, cards: col.cards.filter((x) => x.id !== card.id) };
      if (col.id === paraId) return { ...col, cards: [card, ...(col.cards || [])] };
      return col;
    });
    const destino = novas.find((c) => c.id === paraId);
    setColumns({ ...columns, columns: novas });
    if (ocultas.includes(paraId)) setOcultas((o) => o.filter((id) => id !== paraId));
    setAberto((a) => (a && a.card.id === card.id ? { ...a, colunaId: paraId } : a));
    updatePositionAndStatus(paraId, destino.cards.map((item, index) => ({ id_lead: item.id, position: index })), card, backup)
      .then((ok) => {
        if (ok) toast.success(`${card.title} movido para ${destino.title}`);
        else setAberto((a) => (a && a.card.id === card.id ? { ...a, colunaId: origem.id } : a));
      });
  };

  const [menuMover, setMenuMover] = useState(null); // { ancora, card, colunaId }

  /* Exibição ------------------------------------------------------------ */
  const visiveis = useMemo(
    () => ({ ...columns, columns: (columns.columns || []).filter((c) => !ocultas.includes(c.id)) }),
    [columns, ocultas]
  );
  const colunasOcultas = (columns.columns || []).filter((c) => ocultas.includes(c.id));
  const totalCards = (columns.columns || []).reduce((s, c) => s + (c.cards ? c.cards.length : 0), 0);

  const abrir = (card, colunaId) => setAberto({ card, colunaId });

  const BadgeFollow = ({ valor }) => {
    const s = situacaoFollowUp(valor);
    if (!s) return null;
    return (
      <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap", ...TOM_FOLLOW[s.tom] }}>
        {s.texto}
      </span>
    );
  };

  const Avatar = ({ assessor }) => (
    <Tooltip title={assessor ? assessor.name : "Sem assessor"}>
      <span
        aria-label={assessor ? `Assessor: ${assessor.name}` : "Sem assessor"}
        style={{
          width: 26, height: 26, borderRadius: 13, flex: "none",
          background: assessor ? CORES.verdeEscuro : "#8A9384", color: "#fff",
          fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {assessor ? iniciais(assessor.name) : "—"}
      </span>
    </Tooltip>
  );

  const renderCard = (card) => {
    const colunaId = (columns.columns || []).find((c) => (c.cards || []).some((x) => x.id === card.id))?.id;
    return (
      <div
        style={{
          background: "#fff", width: 250, boxSizing: "border-box", padding: 10, marginBottom: 8,
          borderRadius: 8, border: `1px solid ${CORES.borda}`,
        }}
      >
        <button
          type="button"
          onClick={() => abrir(card, colunaId)}
          style={{ all: "unset", cursor: "pointer", display: "block", fontWeight: 700, fontSize: 14, color: CORES.texto, overflowWrap: "anywhere" }}
          title="Abrir resumo do lead"
        >
          {card.title}
        </button>
        {card.content && <div style={{ fontSize: 12, color: CORES.textoSuave, marginTop: 2 }}>{card.content}</div>}
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <ContatosLead leadId={card.lead_id} mailingLeadsId={card.mailing_leads_id} />
          {pode("editar_leads") && (
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: "none" }}
              startIcon={<SwapHorizIcon fontSize="small" />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setMenuMover({ ancora: e.currentTarget, card, colunaId }); }}
              aria-label={`Mover ${card.title} para outra coluna`}
            >
              Mover
            </Button>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 6 }}>
          <BadgeFollow valor={card.follow_up} />
          <span style={{ marginLeft: "auto" }}><Avatar assessor={assessorDoCard(card)} /></span>
        </div>
      </div>
    );
  };

  const renderColumnHeader = (col) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "2px 4px 8px" }}>
      <b style={{ fontSize: 14 }}>{col.title}</b>
      <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span style={{ background: "#fff", borderRadius: 10, padding: "0 8px", fontSize: 12, color: CORES.textoSuave, lineHeight: "20px" }}>
          {(col.cards || []).length}
        </span>
        <Tooltip title="Recolher coluna">
          <IconButton size="small" aria-label={`Recolher coluna ${col.title}`} onClick={() => setOcultas((o) => o.concat(col.id))}>
            <VisibilityOffOutlinedIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </span>
    </div>
  );


  // Gestor/master (visao_gestao) filtram por qualquer assessor.
  // Assessor logado filtra só pelo próprio nome.
  const veTodosAssessores = pode("visao_gestao");
  const idAssessorLogado = localStorage.getItem("id_assessor");
  const opcoesFiltroAssessor = veTodosAssessores
    ? assessores
    : (idAssessorLogado
        ? [{ id: Number(idAssessorLogado), name: localStorage.getItem("name") || "Meus leads" }]
        : []);

  return (
    <Container maxWidth={false} style={{ maxWidth: 1600 }}>
      <PageHeader
        titulo="Gestão do Assessor"
        acoes={
          <>
            <ButtonGroup size="small" aria-label="Visualização">
              <Button variant={visao === "board" ? "contained" : "outlined"} color={visao === "board" ? "primary" : "default"} onClick={() => setVisao("board")}>Board</Button>
              <Button variant={visao === "lista" ? "contained" : "outlined"} color={visao === "lista" ? "primary" : "default"} onClick={() => setVisao("lista")}>Lista</Button>
            </ButtonGroup>
            {pode("editar_leads") && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} component={Link} to="/leads/novo">
                Novo lead
              </Button>
            )}
          </>
        }
      />

      <FilterBar
        onPesquisar={() => buscar()}
        onLimpar={limpar}
        ativos={ativos}
        extra={<Button onClick={() => setMaisFiltros((v) => !v)}>{maisFiltros ? "Menos filtros" : "Mais filtros"}</Button>}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField id="filtro_razao" label="Razão social" value={filtros.razao} onChange={(e) => alterar("razao", e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              id="filtro_cliente"
              options={customers}
              value={filtros.cliente}
              onChange={(e, v) => setFiltros((f) => ({ ...f, cliente: v, mailing: null }))}
              getOptionLabel={(o) => (o && o.name) || ""}
              getOptionSelected={(o, v) => o && v && o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Cliente/projeto" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              id="filtro_mailing"
              options={mailings}
              value={filtros.mailing}
              onChange={(e, v) => {
                // Escolher o mailing já carrega o board, sem clicar em Pesquisar.
                const novo = { ...filtros, mailing: v };
                setFiltros(novo);
                if (v?.id) buscar(novo);
              }}
              getOptionLabel={(o) => (o && o.name) || ""}
              getOptionSelected={(o, v) => o && v && o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Mailing" />}
            />
          </Grid>
          {(veTodosAssessores || idAssessorLogado) && (
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                id="filtro_assessor"
                options={opcoesFiltroAssessor}
                value={filtros.assessor}
                onChange={(e, v) => alterar("assessor", v)}
                getOptionLabel={(o) => (o && o.name) || ""}
                getOptionSelected={(o, v) => String(o?.id) === String(v?.id)}
                renderInput={(params) => <TextField {...params} label="Assessor" />}
              />
            </Grid>
          )}
        </Grid>
        <Collapse in={maisFiltros}>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                id="filtro_estado"
                options={ESTADOS}
                value={ESTADOS.find((e) => e.sigla === filtros.estado) || null}
                onChange={(e, v) => alterar("estado", v?.sigla || "")}
                getOptionLabel={(o) => `${o.sigla} - ${o.nome}`}
                renderInput={(params) => <TextField {...params} label="Estado" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField id="filtro_cidade" label="Cidade" value={filtros.cidade} onChange={(e) => alterar("cidade", e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField id="filtro_follow" label="Follow-up" type="date" InputLabelProps={{ shrink: true }} value={filtros.followUp} onChange={(e) => alterar("followUp", e.target.value)} fullWidth />
            </Grid>
          </Grid>
        </Collapse>
      </FilterBar>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontSize: 13, color: CORES.textoSuave, margin: "4px 0 10px" }}>
        <span>{totalCards} leads</span>
        {filtroSalvo?.mailing && filtros.mailing?.id === filtroSalvo.mailing.id && (
          <span data-testid="filtro-lembrado">
            Abrindo no último mailing usado ·{" "}
            <a href="#limpar" onClick={(e) => { e.preventDefault(); limpar(); }} style={{ color: CORES.verdeEscuro }}>limpar</a>
          </span>
        )}
        {colunasOcultas.length > 0 && (
          <span>
            Colunas recolhidas:{" "}
            {colunasOcultas.map((c) => (
              <Button key={c.id} size="small" onClick={() => setOcultas((o) => o.filter((id) => id !== c.id))}>
                {c.title} ({(c.cards || []).length}) · mostrar
              </Button>
            ))}
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", gap: 12 }} aria-label="Legenda do follow-up">
          <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#A63A1A", marginRight: 4 }} />Follow-up atrasado</span>
          <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#9A6100", marginRight: 4 }} />Follow-up em até 7 dias</span>
          <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#2F6420", marginRight: 4 }} />Follow-up depois de 7 dias</span>
        </span>
      </div>

      <div style={{ height: 4 }}>{loading && <LinearProgress />}</div>

      {visao === "board" ? (
        <div style={{ overflowX: "auto", paddingBottom: 8 }}>
          <Board
            key="board"
            children={visiveis}
            disableCardDrag={!pode("editar_leads")}
            onCardDragEnd={onCardDragEnd}
            renderColumnHeader={renderColumnHeader}
            renderCard={(card) => renderCard(card)}
          />
        </div>
      ) : (
        <Paper variant="outlined" style={{ overflowX: "auto" }}>
          <Table size="small" aria-label="Leads em lista">
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mailing</TableCell>
                <TableCell>Follow-up</TableCell>
                <TableCell>Assessor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(visiveis.columns || []).flatMap((col) => (col.cards || []).map((card) => (
                <TableRow key={card.id} hover style={{ cursor: "pointer" }} onClick={() => abrir(card, col.id)}>
                  <TableCell style={{ fontWeight: 600 }}>{card.title}</TableCell>
                  <TableCell><StatusChip tipo="lead" valor={col.id} /></TableCell>
                  <TableCell>{card.content}</TableCell>
                  <TableCell><BadgeFollow valor={card.follow_up} /></TableCell>
                  <TableCell>{assessorDoCard(card)?.name || "—"}</TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Menu
        anchorEl={menuMover ? menuMover.ancora : null}
        open={!!menuMover}
        onClose={() => setMenuMover(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem disabled style={{ fontSize: 12, opacity: 0.8 }}>Mover para</MenuItem>
        {(columns.columns || []).filter((c) => menuMover && c.id !== menuMover.colunaId).map((c) => (
          <MenuItem key={c.id} onClick={() => { const m = menuMover; setMenuMover(null); moverPara(m.card, c.id); }}>
            {c.title}
          </MenuItem>
        ))}
      </Menu>

      <LeadDrawer
        card={aberto ? aberto.card : null}
        colunaId={aberto ? aberto.colunaId : undefined}
        colunas={(columns.columns || []).map((c) => ({ id: c.id, title: c.title }))}
        onMover={moverPara}
        assessores={assessores}
        assessorAtual={aberto ? assessorDoCard(aberto.card) : null}
        onFechar={() => setAberto(null)}
        onTrocarAssessor={(card, novo) => {
          trocarAssessor(card, novo);
          setAberto((a) => (a ? { ...a, card: { ...a.card, id_assessor: novo?.id || null, assessor_name: novo?.name || "" } } : a));
        }}
      />
    </Container>
  );
};

export default GestaoAssessor;
